import { BasicDialog, IOverflowListProps, OverflowList } from '@pinpointhq/thumbtack';
import { uniqueId } from 'lodash';
import * as React from 'react';
import { usePopper } from 'react-popper';
import { IUseVisibilityHook, useVisibility } from '../../shared/hooks/useVisibility';

interface IListItem {
  label: string;
  href: string;
  hasSubNavigationMenu: boolean;
  subNavigationItems: { href: string; name: string }[];
}

interface IOverflowedItems {
  items: IListItem[];
  overflowedItems: IListItem[];
  overflowVisibility: IUseVisibilityHook;
}

function OverflowedItems(props: IOverflowedItems) {
  const { items, overflowedItems, overflowVisibility } = props;

  const allItemsOverflowed = items.length === overflowedItems.length;

  // If you hit enter when the menu item is focused we want to open the menu so that the site can be navigated with
  // keyboard only
  //
  const handleKeyDown = (event) => {
    // 13 is enter and 32 is space
    //
    if (event.which === 13 || event.which === 32) {
      overflowVisibility.handleOpen();
    }
  };

  return (
    // An <a> tag with no href will be skipped over by tabbing (which in this case we don't want for accessibility) so
    // we are manually adding a tabIndex to ensure it isn't missed
    //
    <a
      className="careers-navigation-block__menu-item"
      onKeyDown={handleKeyDown}
      onClick={overflowVisibility.handleOpen}
      tabIndex={0}
      role="menu"
    >
      {allItemsOverflowed ? 'Menu' : 'More...'}
    </a>
  );
}

interface IVerticalMenuItem {
  item: IListItem;
}

function VerticalMenuItem(props: IVerticalMenuItem) {
  const { item } = props;

  const verticalSubNavItems = item.subNavigationItems.map((subItem, index) => {
    return (
      <div key={index}>
        <a className="careers-navigation-overflow-menu__sub-menu-item frow frow--centered-column" href={subItem.href}>
          {subItem.name}
        </a>
      </div>
    );
  });

  return (
    <div>
      <a className="careers-navigation-overflow-menu__menu-item" href={item.href} key={item.label}>
        {item.label}
      </a>
      <div className="mar-b-2">{verticalSubNavItems}</div>
    </div>
  );
}

interface IOverflowMenuDialog {
  items: IListItem[];
  overflowVisibility: IUseVisibilityHook;
}

function OverflowMenuDialog(props: IOverflowMenuDialog) {
  const { items, overflowVisibility } = props;

  const handleKeyPress = (event) => {
    if (event.which === 13) {
      overflowVisibility.handleClose();
    }
  };

  return (
    <BasicDialog
      portalClassName="careers-navigation-overflow-menu"
      backdropClassName="careers-navigation-overflow-menu__backdrop"
      className="careers-navigation-overflow-menu__dialog"
      isOpen={overflowVisibility.isOpen}
      onClose={overflowVisibility.handleClose}
      canOutsideClickClose={true}
      canEscapeKeyClose={true}
    >
      <div className="frow frow--centered-column">
        {items.map((item) => (
          <VerticalMenuItem {...{ key: item.label, item }} />
        ))}
        <div>
          {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
          <a
            className="careers-navigation-overflow-menu__menu-item"
            onClick={overflowVisibility.handleClose}
            onKeyDown={handleKeyPress}
            tabIndex={0}
          >
            Close menu
          </a>
        </div>
      </div>
    </BasicDialog>
  );
}

interface IHorizontalMenuItem {
  item: IListItem;
}

function HorizontalMenuItem(props: IHorizontalMenuItem) {
  const [visible, setVisibility] = React.useState(false);

  const [referenceRef, setReferenceRef] = React.useState(null);
  const [popperRef, setPopperRef] = React.useState(null);
  const [selectedMenuItemIndex, setSelectedMenuItemIndex] = React.useState<number | null>(-1);

  const { styles, attributes } = usePopper(referenceRef, popperRef, {
    placement: 'bottom-start',
  });

  function handleDropdownClick(event) {
    setVisibility(!visible);
  }

  function handleMouseEnter(event) {
    setVisibility(true);
  }

  function handleMouseLeave(event) {
    setVisibility(false);
  }

  // NOTE(BR): For accessibility we don't close the dropdown until focus leaves the dropdown as a whole (the parent
  // component or any of the children)
  //
  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setVisibility(false);
      setSelectedMenuItemIndex(-1);
    }
  };

  const { item } = props;
  const id = uniqueId();

  const subNavigationItems = item.subNavigationItems.map((subItem, index) => {
    return (
      <div
        style={{
          display: `${visible ? 'flex' : 'none'}`,
        }}
        key={index}
      >
        <a
          className="careers-navigation-block__sub-nav-menu theme-highlight-background-color-ie-override"
          style={{ width: '100%' }}
          href={subItem.href}
          tabIndex={-1}
        >
          {subItem.name}
        </a>
      </div>
    );
  });

  const handleKeyPress: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Escape') {
      setVisibility(false);
      setSelectedMenuItemIndex(-1);
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.stopPropagation();
    event.preventDefault();

    moveSelectedSubMenuSelection(event.key === 'ArrowDown' ? 'down' : 'up');
  };

  const moveSelectedSubMenuSelection = (direction: 'up' | 'down') => {
    const totalLength = item.subNavigationItems.length;
    const index = direction === 'up' ? selectedMenuItemIndex - 1 : selectedMenuItemIndex + 1;
    if (direction === 'up' && index <= -1) return;
    if (direction === 'down' && index > totalLength - 1) return;

    const subMenuElements = document
      .getElementById(id)
      .getElementsByClassName('careers-navigation-block__sub-nav-menu')[index];
    (subMenuElements as any)?.focus();
    setSelectedMenuItemIndex(index);
  };

  return (
    <>
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
      {item.hasSubNavigationMenu ? (
        <div
          className="careers-navigation-block__menu-item careers-navigation-block__hoverzone"
          key={item.label}
          ref={setReferenceRef}
          onClick={handleDropdownClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          tabIndex={0}
          onFocus={() => setVisibility(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
          id={id}
          role="button"
        >
          {item.label}
          <div ref={setPopperRef} style={{ ...styles.popper, zIndex: 50 }} {...attributes.popper}>
            {subNavigationItems}
          </div>
          <i className="fas fa-chevron-down mar-l-1" />
        </div>
      ) : (
        <a className="careers-navigation-block__menu-item" href={item.href} key={item.label}>
          {item.label}
        </a>
      )}
    </>
  );
}

interface IResponsiveNavigation {
  menuItems: IListItem[];
}

export default function ResponsiveNavigation(props: IResponsiveNavigation) {
  const { menuItems: items } = props;
  const overflowVisibility = useVisibility();

  const listProps: IOverflowListProps<IListItem> = {
    collapseFrom: 'end',
    items,
    observeParents: true,
    overflowRenderer: (overflowedItems) => <OverflowedItems {...{ items, overflowedItems, overflowVisibility }} />,
    visibleItemRenderer: (item) => <HorizontalMenuItem {...{ key: item.label, item }} />,
  };

  return (
    <>
      <OverflowList<IListItem> {...listProps} />
      <OverflowMenuDialog {...{ items, overflowVisibility }} />
    </>
  );
}
